import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { ServerIcon, Badge } from "@libs/rubick";
import { InformationCircleIcon, MapPinIcon, CpuChipIcon, ClockIcon, CubeTransparentIcon } from "@heroicons/react/24/outline";
import { NodeHealthStatus } from "../types";
import { NodeType } from "@libs/appliance_common";
import { NodeDetailsContainer } from "./NodeDetailsContainer";
const STATUS_CONFIG = {
    [NodeHealthStatus.Healthy]: {
        color: "green",
        label: "Healthy"
    },
    [NodeHealthStatus.Error]: {
        color: "red",
        label: "Error"
    },
    [NodeHealthStatus.Offline]: {
        color: "zinc",
        label: "Offline"
    }
};
const formatTimeAgo = (dateStr)=>{
    const date = new Date(dateStr);
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};
const NodeTypeIcon = ({ type, className })=>{
    if (type === NodeType.AIAppliance) {
        return /*#__PURE__*/ _jsx(CpuChipIcon, {
            className: className
        });
    }
    return /*#__PURE__*/ _jsx(ServerIcon, {
        className: className
    });
};
export function NodeListItem({ node }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const { color: statusColor, label: statusLabel } = STATUS_CONFIG[node.healthStatus];
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-gray-50 border border-gray-200 rounded-lg hover:shadow-md transition-shadow",
        children: [
            /*#__PURE__*/ _jsxs("button", {
                className: "flex items-center justify-between w-full text-left p-6",
                onClick: ()=>setIsExpanded(!isExpanded),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-4 flex-1",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "bg-gray-100 p-3 rounded-full",
                                children: /*#__PURE__*/ _jsx(NodeTypeIcon, {
                                    type: node.nodeType,
                                    className: "w-6 h-6"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "font-semibold text-gray-900 text-base",
                                        children: node.nodeId
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-3 mt-1 text-sm text-gray-600",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx(InformationCircleIcon, {
                                                        className: "w-4 h-4"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        children: node.description
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx(MapPinIcon, {
                                                        className: "w-4 h-4"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        children: node.location
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx(NodeTypeIcon, {
                                                        type: node.nodeType,
                                                        className: "w-4 h-4"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        children: node.nodeType
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx(ClockIcon, {
                                                        className: "w-4 h-4"
                                                    }),
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        children: [
                                                            "Last check: ",
                                                            formatTimeAgo(node.lastCheck)
                                                        ]
                                                    })
                                                ]
                                            }),
                                            node.controlPlane && /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1 text-indigo-600",
                                                children: [
                                                    /*#__PURE__*/ _jsx(CubeTransparentIcon, {
                                                        className: "w-4 h-4"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        children: "Control Plane"
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-4",
                        children: [
                            /*#__PURE__*/ _jsx(Badge, {
                                color: statusColor,
                                children: statusLabel
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: `w-6 h-6 transform transition-transform duration-300 ${isExpanded ? "rotate-180" : "rotate-0"}`,
                                children: /*#__PURE__*/ _jsx(ChevronDownIcon, {})
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: `transition-[max-height] duration-500 ease-in ${isExpanded ? "max-h-screen overflow-y-auto" : "max-h-0 overflow-hidden"}`,
                children: isExpanded && /*#__PURE__*/ _jsx(NodeDetailsContainer, {
                    nodeId: node.nodeId
                })
            })
        ]
    });
}
